import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/authContext";

export const ROLES = [
    { id: "fan", label: "Fan" },
    { id: "artist_owner", label: "Artist Owner" },
    { id: "artist_manager", label: "Artist Manager" },
    { id: "artist_team", label: "Team Member" },
    { id: "event_staff", label: "Event Staff" },
    { id: "admin", label: "Platform Admin" },
];

/**
 * Map an artist_members.role value → the workspace-role token the frontend
 * navigation and editor screens have historically checked. This is the
 * ONLY place we translate membership → role, so the rest of the app can
 * treat `role` as a single well-defined value.
 *
 * IMPORTANT: `manager` (Artist Manager/Admin) receives the same operational
 * access as `owner` inside the workspace surface. Ownership itself remains
 * distinct in labels + backend permission checks.
 */
const MEMBERSHIP_TO_ROLE = {
    owner: "artist_owner",
    manager: "artist_owner", // full management, non-ownership
    team: "artist_team",
    staff: "event_staff",
    admin: "admin",
};

const RoleContext = createContext(null);

export const RoleProvider = ({ children }) => {
    const { user, artists } = useAuth();
    const [activeArtistId, setActiveArtistId] = useState(() => localStorage.getItem("pm_active_artist_id") || null);
    const [activeArtistName, setActiveArtistName] = useState(() => localStorage.getItem("pm_active_artist_name") || "");

    // Global fallback ONLY. Under the universal-account model this is
    // usually "fan" — do NOT gate workspace features on it directly.
    const globalRole = useMemo(
        () => (user && typeof user === "object" && user.role) || "fan",
        [user],
    );

    // Whenever the artists list changes, keep activeArtistId in sync with
    // something the user still has access to.
    useEffect(() => {
        if (!artists || artists.length === 0) return;
        const stillHasAccess = activeArtistId && artists.find((a) => a.id === activeArtistId);
        if (stillHasAccess) {
            if (stillHasAccess.name !== activeArtistName) {
                setActiveArtistName(stillHasAccess.name);
                localStorage.setItem("pm_active_artist_name", stillHasAccess.name);
            }
            return;
        }
        const first = artists[0];
        setActiveArtistId(first.id);
        setActiveArtistName(first.name);
        localStorage.setItem("pm_active_artist_id", first.id);
        localStorage.setItem("pm_active_artist_name", first.name);
    }, [artists, activeArtistId, activeArtistName]);

    const activeArtist = useMemo(
        () => (activeArtistId && Array.isArray(artists))
            ? artists.find((a) => a.id === activeArtistId)
            : null,
        [artists, activeArtistId],
    );

    // The effective workspace role. Priority: platform admin → active
    // artist membership → global user.role → "fan".
    const membershipRole = activeArtist?.role || null;
    const role = useMemo(() => {
        if (globalRole === "admin") return "admin";
        if (membershipRole && MEMBERSHIP_TO_ROLE[membershipRole]) {
            return MEMBERSHIP_TO_ROLE[membershipRole];
        }
        return globalRole;
    }, [globalRole, membershipRole]);

    const setActiveArtist = (id, name) => {
        setActiveArtistId(id);
        setActiveArtistName(name);
        localStorage.setItem("pm_active_artist_id", id);
        localStorage.setItem("pm_active_artist_name", name);
    };

    // Dev-only impersonation setter (admins only, ignored otherwise)
    const setRoleImpersonation = (r) => {
        if (globalRole !== "admin") return;
        localStorage.setItem("pm_role_dev", r);
    };
    const effectiveRole = globalRole === "admin" ? (localStorage.getItem("pm_role_dev") || "admin") : role;

    return (
        <RoleContext.Provider
            value={{
                role: effectiveRole,
                realRole: role,
                globalRole,
                membershipRole,
                setRole: setRoleImpersonation,
                artists,
                activeArtistId,
                activeArtistName,
                activeArtist,
                setActiveArtist,
            }}
        >
            {children}
        </RoleContext.Provider>
    );
};

export const useRole = () => {
    const ctx = useContext(RoleContext);
    if (!ctx) throw new Error("useRole must be inside RoleProvider");
    return ctx;
};
